import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { ReactComponent as FireIcon } from '../../../../../assets/icons/trainings/fire.svg';
import EditActionButton from '../../../../../components/designe/ActionButtons/EditActionButton';
import SaveActionButton from '../../../../../components/designe/ActionButtons/SaveActionButton';
import AppDialog from '../../../../../components/reusable/app-dialog/AppDialog';
import ClickableExercise from '../../../../../components/reusable/cliackable-exercise/ClickableExercise';
import SaveSet from '../../../../../components/reusable/set-group-edit-table/SetEdit/save-set/SaveSet';
import { useElementWidth } from '../../../../../hooks/useElementWidth';
import { usePutTrainingSetLoad } from '../../../../../redux/api/trainingsAPI';
import {
  sideColumnWidth,
  typeColumnWidth,
} from '../../../../../shared/constants';
import { IExerciseOption } from '../../../../../types/interfaces/exercise-option';
import { ITrainingSet } from '../../../../../types/interfaces/training-set';
import { ITrainingSetItem } from '../../../../../types/interfaces/training-set-item';
import { ITrainingSetItemOption } from '../../../../../types/interfaces/training-set-item-option';
import { orderBy } from '../../../../../utils/collections/array-utils';
import {
  getSetItemKey,
  orderOptions,
} from '../../../../../utils/training/set-utils';
import {
  getColumnOptions,
  getColumnWidth,
} from '../../../../../utils/ui/column-size-utils';
import EditManualLoad from '../../../current-training/current-training-set/edit-manual-load/EditManualLoad';
import FinishedTrainingSetItem, {
  statusColumnWidth,
} from '../finished-training-set-item/FinishedTrainingSetItem';
import { useStyles } from './FinishedTrainingSetList.styles';
import { useFinishedTrainingSetList } from './useFinishedTrainingSetList';

const roundToTwoDecimals = (value: number) => {
  return Math.round(value * 100) / 100;
};

function FinishedTrainingSetList({
  trainingSet,
  exerciseOptions,
  trainingId,
  canEdit = true,
}: {
  trainingSet: ITrainingSet;
  exerciseOptions: IExerciseOption[];
  trainingId: string;
  canEdit?: boolean;
}) {
  const { t } = useTranslation();

  const theme = useTheme();

  const { classes } = useStyles(theme);

  const { showSaveSetModal, setShowSaveSetModal, onSaveSet } =
    useFinishedTrainingSetList(trainingSet);

  const [showEditLoadModal, setShowEditLoadModal] = useState(false);
  const [putTrainingSetLoad] = usePutTrainingSetLoad();

  const { ref, width } = useElementWidth();
  const orderedExerciseOptions = orderBy(
    trainingSet?.exercise?.options?.length
      ? trainingSet.exercise.options
      : exerciseOptions || [],
    'priorityOrder',
  );
  const columnWidth = getColumnWidth(
    width,
    trainingSet?.exercise?.options?.length,
    typeColumnWidth +
      (!trainingSet?.exercise?.splitBySide ? 0 : sideColumnWidth) +
      statusColumnWidth,
    0,
  );

  const handleSaveManualLoad = async (manualLoad: number | null) => {
    await putTrainingSetLoad({
      trainingId,
      setId: trainingSet.id,
      manualLoad,
    }).unwrap();
    setShowEditLoadModal(false);
  };

  function getNormalizedTrainingSetItem(
    trainingSetItem: ITrainingSetItem,
  ): ITrainingSetItem {
    const orderedSetOptions = orderOptions(
      trainingSetItem?.trainingSetItemOptions || [],
      orderedExerciseOptions,
    );
    const optionById = new Map(
      orderedSetOptions.map((setOption) => [setOption.optionId, setOption]),
    );
    const completedOptions: ITrainingSetItemOption[] =
      orderedExerciseOptions.map(
        (option) =>
          optionById.get(option.id) || {
            optionId: option.id,
            value: null,
            value2: null,
            programValue: null,
            programValue2: null,
          },
      );

    return {
      ...trainingSetItem,
      trainingSetItemOptions: completedOptions,
    };
  }

  function renderExerciseOptionHeaders() {
    return (
      <Box
        ref={ref}
        display="flex"
        alignItems="center"
        className={classes.trainingSetItemListHeader}>
        <Box
          {...getColumnOptions(typeColumnWidth)}
          className={classes.headerTextWrapper}>
          <Typography variant="label-text-md"> {t('common.type')}</Typography>
        </Box>
        {trainingSet?.exercise?.splitBySide && (
          <Box
            {...getColumnOptions(sideColumnWidth)}
            className={classes.headerTextWrapper}
          />
        )}
        {orderedExerciseOptions.map((option) => {
          return (
            <Box
              {...getColumnOptions(columnWidth)}
              key={option.id}
              className={classes.headerTextWrapper}>
              <Typography variant="label-text-md"> {option.name}</Typography>
            </Box>
          );
        })}
        <Box
          {...getColumnOptions(statusColumnWidth)}
          className={classes.headerTextWrapper}
        />
      </Box>
    );
  }

  return (
    <Box key={trainingSet.id}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        padding={1}>
        <ClickableExercise
          exercise={trainingSet.exercise}
          fromTrainingId={trainingId}
        />
        {false && (
          <SaveActionButton onClick={() => setShowSaveSetModal(true)} />
        )}
      </Box>
      <Box className={classes.trainingSetItemList}>
        {renderExerciseOptionHeaders()}
        {orderBy(trainingSet.trainingSetItems, 'orderNumber').map(
          (trainingSetItem: ITrainingSetItem) => {
            const normalizedTrainingSetItem =
              getNormalizedTrainingSetItem(trainingSetItem);

            return (
              <FinishedTrainingSetItem
                key={getSetItemKey(normalizedTrainingSetItem)}
                trainingId={trainingId}
                trainingSet={trainingSet}
                trainingSetId={trainingSet.id}
                trainingSetItem={normalizedTrainingSetItem}
                columnWidth={columnWidth}
              />
            );
          },
        )}
        <Box display="flex" alignItems="center" margin={1} gap={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box display="flex" alignItems="center" gap={0.25}>
              <FireIcon className={classes.fireIcon} />
              <Typography variant="label-text-md">
                {t('trainings.totalLoad')}:{' '}
              </Typography>
            </Box>
            <Typography variant="label-text-md">
              {trainingSet.manualLoad !== null &&
              trainingSet.manualLoad !== undefined
                ? roundToTwoDecimals(trainingSet.manualLoad)
                : roundToTwoDecimals(trainingSet.load) || '-'}
            </Typography>
            {canEdit ? (
              <EditActionButton onClick={() => setShowEditLoadModal(true)} />
            ) : null}
          </Box>
        </Box>
      </Box>
      <AppDialog
        open={!!showSaveSetModal}
        onClose={() => setShowSaveSetModal(false)}
        title={t('setTable.saveSet')}>
        <SaveSet
          onCancel={() => setShowSaveSetModal(false)}
          onSaveSet={(title: string) => onSaveSet(title)}
        />
      </AppDialog>
      <AppDialog
        open={showEditLoadModal}
        onClose={() => setShowEditLoadModal(false)}
        title={t('trainings.manualLoad')}>
        <EditManualLoad
          onCancel={() => setShowEditLoadModal(false)}
          onSave={handleSaveManualLoad}
          currentValue={
            trainingSet.manualLoad !== null &&
            trainingSet.manualLoad !== undefined
              ? trainingSet.manualLoad
              : trainingSet.load
          }
        />
      </AppDialog>
    </Box>
  );
}

export default FinishedTrainingSetList;
